import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import storeLoader from 'app/stores/storeLoader';
import { Button, FlexColumn, Text, Tooltip, MenuItem } from 'core/components';
import { POLICY_APPLICATION_LABELS, SUPPRESSION_TYPES } from '@kentik/ui-shared/alerting/constants';
import { getTopLevelApplication, checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import { hasDimensionValue } from '@kentik/ui-shared/query/query';
import { withRouter } from 'react-router-dom';
import { buildKeyValueFilters } from 'app/forms/config/suppressions/suppression';

@withRouter
@storeLoader('$suppressions')
@inject('$auth', '$alerting', '$dictionary', '$metrics')
@observer
export default class SuppressAlertButton extends Component {
  static defaultProps = {
    comment: '',
    disabled: false
  };

  filterDimensions = (dimensionsToKeyPart) =>
    Object.entries(dimensionsToKeyPart).filter(([, value]) => hasDimensionValue(value));

  getDimensionLabel = (dimensionKey) => {
    const { $metrics, $dictionary, alertModel } = this.props;

    if (alertModel.policy?.isMetricPolicy) {
      return $metrics.getDimensionLabelForPolicy(alertModel.policy, dimensionKey);
    }

    return $dictionary.dimensionLabelsMap[dimensionKey]?.label || dimensionKey;
  };

  handleCreateSuppression = () => {
    const { $suppressions, $metrics, alertModel, history, location } = this.props;
    const { id, ruleId, policyName, pattern } = alertModel;
    const { measurement, application, dimensionToKeyPart } = pattern;
    const severity = alertModel.get('severity');
    const topLevelApp = getTopLevelApplication(application);
    const applicationMetadata = { type: SUPPRESSION_TYPES.CONDITION };
    const filters = this.filterDimensions(dimensionToKeyPart).map(([filterField, filterValue]) => ({
      filterField,
      filterOperator: 'equals',
      filterValue
    }));

    const data = {
      key_filter: { filters: buildKeyValueFilters(filters) },
      comment: `${POLICY_APPLICATION_LABELS[topLevelApp] || application}: "${policyName || ''}" (Alert ID: ${id})`,
      strict: false,
      rule_ids: ruleId ? [ruleId] : [],
      severities: severity ? [severity] : [],
      applications: [application],
      uiProperties: { startAsSoonAsPossible: true, applicationMetadata }
    };

    if (checkApplication.isMetric(application)) {
      applicationMetadata.type = SUPPRESSION_TYPES.METRIC;
      applicationMetadata.measurement = measurement;
    }

    if (checkApplication.isNms(application)) {
      applicationMetadata.type = SUPPRESSION_TYPES.NMS;
      applicationMetadata.measurement = measurement;
    }

    data.application_metadata = JSON.stringify(applicationMetadata, null, 2);
    const suppression = $suppressions.collection.build(data, { deserialize: false });

    history.push('/v4/settings/suppressions/new', {
      suppressionDraft: $suppressions.buildCloneDraft(suppression, $metrics),
      returnLocation: location
    });
  };

  handleViewSuppression = () => {
    const { history, location } = this.props;

    history.push(`/v4/settings/suppressions/${encodeURIComponent(this.matchingSuppression.id)}`, {
      returnLocation: location
    });
  };

  get matchingSuppression() {
    const { $suppressions, alertModel } = this.props;
    const dimensionToKeyPart = alertModel.get('dimensionToKeyPart');
    const { application } = alertModel.pattern;
    const severity = alertModel.get('severity');

    return $suppressions.collection.unfiltered.find((suppression) => {
      const { expired, rule_ids } = suppression;
      const applications = suppression.get('applications') || [];
      const severities = suppression.get('severities') || [];

      if (
        expired ||
        (rule_ids.length && !rule_ids.includes(alertModel.ruleId)) ||
        (applications.length && !applications.includes(application)) ||
        (severities.length && !severities.includes(severity))
      ) {
        return false;
      }

      return suppression.matchesDimensionTarget(dimensionToKeyPart);
    });
  }

  // eslint-disable-next-line no-restricted-syntax
  renderTooltipText = () => {
    const { alertModel } = this.props;

    if (alertModel.isSuppressed && !this.matchingSuppression) {
      return <Text>Alert is already suppressed.</Text>;
    }

    if (this.matchingSuppression) {
      const { formattedStartTime, startDetails, expirationDetails } = this.matchingSuppression;

      return startDetails.started ? (
        <>
          <Text>Alert is already suppressed:</Text>
          <Text>
            <b>Started:</b> {formattedStartTime} ({startDetails.diff} ago)
          </Text>
          <Text>
            <b>Ends:</b> {expirationDetails.expirationDay} (in {expirationDetails.duration})
          </Text>
        </>
      ) : (
        <>
          <Text>Alert has a pending suppression:</Text>
          <Text>
            <b>Starts:</b> {formattedStartTime} (in {startDetails.diff})
          </Text>
          <Text>
            <b>Ends:</b> {expirationDetails.expirationDay} (in {expirationDetails.duration})
          </Text>
        </>
      );
    }

    return <Text>Review a new suppression for this alert:</Text>;
  };

  render() {
    const { $auth, alertModel, asMenuItem, buttonProps, disabled } = this.props;
    const dimensionToKeyPart = alertModel.get('dimensionToKeyPart');
    const isAllowedToCreateSuppression = $auth.hasRbacPermissions(['alerting.suppressions::create']);

    if (!dimensionToKeyPart || !alertModel.canSuppress) {
      return null;
    }

    const filteredDimensions = this.filterDimensions(dimensionToKeyPart);
    const { matchingSuppression } = this;
    const text = matchingSuppression ? 'View Suppression' : 'Suppress Alert';
    const handleClick = matchingSuppression ? this.handleViewSuppression : this.handleCreateSuppression;
    const isDisabled = matchingSuppression
      ? disabled
      : alertModel.isSuppressed || !isAllowedToCreateSuppression || disabled;

    const ButtonComp = (
      <Button
        mr={0}
        width="100%"
        icon="pause"
        onClick={handleClick}
        disabled={isDisabled}
        text={text}
        textAlign="left"
        {...buttonProps}
      />
    );

    const MenuItemComp = <MenuItem icon="pause" disabled={isDisabled} text={text} onClick={handleClick} />;

    const ClickElement = asMenuItem ? MenuItemComp : ButtonComp;

    return (
      <Tooltip
        fill
        content={
          <FlexColumn style={{ maxWidth: 300 }}>
            {this.renderTooltipText()}
            {!this.matchingSuppression &&
              filteredDimensions.slice(0, 4).map(([dimensionKey, dimensionValue], index) =>
                index === 3 && filteredDimensions.length > 4 ? (
                  <Text key="plusmore" fontWeight="bold">
                    Plus {filteredDimensions.length - 3} more
                  </Text>
                ) : (
                  <Text key={`${dimensionKey}-${dimensionValue}`}>
                    <b>{this.getDimensionLabel(dimensionKey)}:</b> {dimensionValue}
                  </Text>
                )
              )}
          </FlexColumn>
        }
      >
        {ClickElement}
      </Tooltip>
    );
  }
}
